import {
  CURRENCY_BY_MARKET,
  SEED_MARKETS,
  marketsForScope,
  type AffiliateNetwork,
  type Currency,
  type Market,
  type Merchant,
  type MerchantRegion,
  type Transaction,
  type WelcomeBonusFunding,
} from '../dataProvider/types';

/**
 * The Earnings query/aggregation module - the ONE place margin is computed.
 *
 * Per the data-layer decision (D-earnings), v1 aggregates client-side from the
 * mock provider, but every selector lives HERE so this module is the seam: the
 * late real-data migration swaps these aggregators for a server-side earnings
 * endpoint without the surface changing. The surface only ever consumes the
 * `EarningsView` this module returns; it never sees a raw transaction.
 *
 * THE STATUS VOCABULARY (the backbone divergence, resolved at the root).
 * The Phase-3 brief described qualifying statuses as `confirmed, available, paid`
 * (headline), `pending` (pipeline) and `voided, fraud_held` (excluded). Those are
 * NOT the backbone's transaction statuses. The authoritative `transaction_status`
 * enum (docs/backbone-schema.md) is `pending | confirmed | waiting | rejected |
 * cancelled`; `available` and `voided` exist nowhere, `paid` is a
 * `cashout_request_status` (the member cashing out, not the transaction), and
 * `fraud_held` is an `event_type` (a trust event), not a transaction status.
 *
 * So the brief's economic BUCKETS are mapped onto the real enum rather than
 * inventing statuses the backbone does not have:
 *
 *   headline (confirmed and onward) -> `confirmed`
 *     A transaction has no `available`/`paid` sub-state in the backbone: once it
 *     is `confirmed`, the network commission is earned and the member split is
 *     owed, so the margin is booked. Whether the member has since withdrawn
 *     (`paid`, a cashout concern) does not change the margin. "Confirmed and
 *     onward" therefore collapses to exactly `confirmed`.
 *   pipeline (separate, never blended) -> `pending` + `waiting`
 *     The two in-flight states. This mirrors the dashboard seam, which already
 *     treats `pending + waiting` as the pending figure.
 *   excluded entirely -> `rejected` + `cancelled`
 *     The two terminal-negative states (the brief's voided / fraud_held).
 */

// --- tunables (flagged for review) -----------------------------------------

/**
 * Volume floor: an entity below this many qualifying (confirmed) transactions
 * does not occupy a ranked position; it is listed separately, unranked, so a
 * three-transaction store cannot top the ranking on noise.
 * [REVIEW: VOLUME_FLOOR_TXNS]
 */
export const VOLUME_FLOOR_TXNS = 5;

/**
 * Trend gate: if a market has fewer than this many monthly periods with real
 * confirmed volume, the trend is hidden entirely (no confident lines over a few
 * noisy points). [REVIEW: TREND_MIN_PERIODS]
 */
export const TREND_MIN_PERIODS = 4;

/**
 * Trend window: the last N COMPLETE calendar months plus the current month to
 * date, monthly grain, decoupled from the headline period control.
 * [REVIEW: trend lookback]
 */
export const TREND_LOOKBACK_MONTHS = 6;

// --- status buckets (see the header note) ----------------------------------

/** Statuses whose margin counts toward the headline confirmed figure. */
export const CONFIRMED_STATUSES: readonly Transaction['status'][] = [
  'confirmed',
];
/** In-flight statuses shown only as the separate pending pipeline. */
export const PENDING_STATUSES: readonly Transaction['status'][] = [
  'pending',
  'waiting',
];
/** Terminal-negative statuses excluded from both headline and pipeline. */
export const EXCLUDED_STATUSES: readonly Transaction['status'][] = [
  'rejected',
  'cancelled',
];

// --- view-model types -------------------------------------------------------

/** Group qualifying rows by their network (default) or by their merchant. */
export type EntityGrain = 'network' | 'merchant';

/** The headline period control (governs the cards and the table, not the trend). */
export type EarningsPeriod = 'mtd' | 'last30' | 'lastMonth';

export const GRAIN_OPTIONS: ReadonlyArray<{ id: EntityGrain; label: string }> =
  [
    { id: 'network', label: 'By network' },
    { id: 'merchant', label: 'By merchant' },
  ];

export const PERIOD_OPTIONS: ReadonlyArray<{
  id: EarningsPeriod;
  label: string;
}> = [
  { id: 'mtd', label: 'Month to date' },
  { id: 'last30', label: 'Last 30 days' },
  { id: 'lastMonth', label: 'Last full calendar month' },
];

/** A live boost surfaced on an entity row ("boosted, ends 2 Jan"). */
export interface EntityBoost {
  label: string;
  endsAt: string;
}

export interface EarningsEntityRow {
  /** Stable key, e.g. `network:5` or `merchant:12`. */
  key: string;
  name: string;
  /** Count of qualifying (confirmed) rows. */
  volume: number;
  commission: number;
  memberSplit: number;
  /** commission - memberSplit. Negative margins are real and are NOT clamped. */
  margin: number;
  /** margin / commission, or null when commission is zero. */
  marginRate: number | null;
  /** A live boost on this entity in this market, or null. */
  boost: EntityBoost | null;
}

export interface EarningsTrendPoint {
  /** Period key, e.g. '2025-01'. */
  period: string;
  /** Short display label, e.g. 'Jan 25'. */
  label: string;
  confirmedMargin: number;
  contribution: number;
  /** True when the bucket has real confirmed volume (drives the gate). */
  hasVolume: boolean;
}

export interface EarningsBand {
  market: Market;
  currency: Currency;
  /** Reconciliation chain: confirmedMargin - welcomeBonus = contribution. */
  confirmedMargin: number;
  welcomeBonus: number;
  contribution: number;
  /** Pending margin, shown beside the chain as pipeline, never inside it. */
  pendingMargin: number;
  /** Count of qualifying (confirmed) rows in the period. */
  volume: number;
  /** Band norm: the median entity margin rate (ranked entities), or null. */
  medianMarginRate: number | null;
  /** Entities at or above the volume floor, sorted by margin rate ascending. */
  ranked: EarningsEntityRow[];
  /** Entities below the volume floor, unranked. */
  lowVolume: EarningsEntityRow[];
  /** The two-line trend, or null when the gate hides it. */
  trend: EarningsTrendPoint[] | null;
}

export interface EarningsView {
  bands: EarningsBand[];
}

export interface EarningsInput {
  transactions: Transaction[];
  merchants: Merchant[];
  merchantRegions: MerchantRegion[];
  affiliateNetworks: AffiliateNetwork[];
  welcomeBonusFundings: WelcomeBonusFunding[];
}

export interface EarningsOptions {
  /** Access scope (UK | US | GLOBAL); decides which market bands render. */
  scope: string;
  grain: EntityGrain;
  period: EarningsPeriod;
  /** Reference clock (epoch ms) for the period windows and the trend. */
  now: number;
}

// --- small helpers ----------------------------------------------------------

const DAY = 86_400_000;
const round2 = (n: number): number => Math.round(n * 100) / 100;
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** The market bands a scope renders: scope -> markets, kept to operating markets. */
export const bandsForScope = (scope: string): Market[] =>
  marketsForScope(scope).filter((m) => SEED_MARKETS.includes(m));

/** Inclusive [start, end] ms window for the headline period. */
function periodWindow(
  period: EarningsPeriod,
  now: number,
): { start: number; end: number } {
  const d = new Date(now);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  switch (period) {
    case 'mtd':
      return { start: Date.UTC(y, m, 1), end: now };
    case 'last30':
      return { start: now - 30 * DAY, end: now };
    case 'lastMonth':
      return { start: Date.UTC(y, m - 1, 1), end: Date.UTC(y, m, 1) - 1 };
  }
}

const inWindow = (ts: number, w: { start: number; end: number }): boolean =>
  ts >= w.start && ts <= w.end;

const margin = (commission: number, memberSplit: number): number =>
  round2(commission - memberSplit);

const median = (values: number[]): number | null => {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
};

// --- aggregation ------------------------------------------------------------

interface Accum {
  name: string;
  commission: number;
  memberSplit: number;
  volume: number;
}

/** Build the full view for the active scope (one band per derived market). */
export function buildEarningsView(
  input: EarningsInput,
  opts: EarningsOptions,
): EarningsView {
  const bands = bandsForScope(opts.scope).map((market) =>
    buildBand(market, input, opts),
  );
  return { bands };
}

function buildBand(
  market: Market,
  input: EarningsInput,
  opts: EarningsOptions,
): EarningsBand {
  const currency = CURRENCY_BY_MARKET[market];
  const regionById = new Map(input.merchantRegions.map((r) => [r.id, r]));
  const networkById = new Map(input.affiliateNetworks.map((n) => [n.id, n]));
  const merchantById = new Map(input.merchants.map((m) => [m.id, m]));

  const txns = input.transactions.filter((t) => t.market === market);
  const window = periodWindow(opts.period, opts.now);
  const inWin = (t: Transaction): boolean =>
    inWindow(Date.parse(t.occurredAt), window);

  const confirmed = txns.filter(
    (t) => inWin(t) && CONFIRMED_STATUSES.includes(t.status),
  );
  const pending = txns.filter(
    (t) => inWin(t) && PENDING_STATUSES.includes(t.status),
  );

  // Band totals. confirmedMargin = sum of entity margins = sum over confirmed
  // rows of (commission - memberSplit); welcome is platform-level and only
  // enters the reconciliation, never an entity row.
  const sumMargin = (rows: Transaction[]): number =>
    round2(
      rows.reduce(
        (acc, t) => acc + Number(t.commissionAmount) - Number(t.cashbackAmount),
        0,
      ),
    );
  const confirmedMargin = sumMargin(confirmed);
  const pendingMargin = sumMargin(pending);
  const welcomeBonus = round2(
    input.welcomeBonusFundings
      .filter(
        (f) => f.market === market && inWindow(Date.parse(f.fundedAt), window),
      )
      .reduce((acc, f) => acc + Number(f.amount), 0),
  );
  const contribution = round2(confirmedMargin - welcomeBonus);

  // Live boosts on this market's regions (a boost is live while it has not ended).
  const liveBoostRegions = input.merchantRegions.filter(
    (r) =>
      r.market === market &&
      r.boost != null &&
      Date.parse(r.boost.endsAt) > opts.now,
  );
  const soonestBoost = (regions: MerchantRegion[]): EntityBoost | null => {
    const boosts = regions
      .map((r) => r.boost)
      .filter((b): b is NonNullable<typeof b> => b != null)
      .sort((a, b) => Date.parse(a.endsAt) - Date.parse(b.endsAt));
    return boosts.length
      ? { label: boosts[0].label, endsAt: boosts[0].endsAt }
      : null;
  };

  // Group confirmed rows into entities by the active grain.
  const accums = new Map<string, Accum>();
  const entityRegions = new Map<string, Set<number>>();
  for (const t of confirmed) {
    const region = regionById.get(t.merchantRegionId);
    let key: string;
    let name: string;
    if (opts.grain === 'network') {
      const nid = region?.networkId ?? null;
      key = nid == null ? 'network:none' : `network:${nid}`;
      name =
        nid == null
          ? 'No network'
          : (networkById.get(nid)?.name ?? `Network ${nid}`);
    } else {
      key = `merchant:${t.merchantId}`;
      name = merchantById.get(t.merchantId)?.name ?? `Merchant ${t.merchantId}`;
    }
    const acc = accums.get(key) ?? {
      name,
      commission: 0,
      memberSplit: 0,
      volume: 0,
    };
    acc.commission += Number(t.commissionAmount);
    acc.memberSplit += Number(t.cashbackAmount);
    acc.volume += 1;
    accums.set(key, acc);
    if (region) {
      const set = entityRegions.get(key) ?? new Set<number>();
      set.add(region.id);
      entityRegions.set(key, set);
    }
  }

  const boostForEntity = (key: string): EntityBoost | null => {
    const regionIds = entityRegions.get(key);
    if (!regionIds) return null;
    return soonestBoost(liveBoostRegions.filter((r) => regionIds.has(r.id)));
  };

  const rows: EarningsEntityRow[] = [...accums.entries()].map(([key, a]) => {
    const commission = round2(a.commission);
    const memberSplit = round2(a.memberSplit);
    const m = margin(commission, memberSplit);
    return {
      key,
      name: a.name,
      volume: a.volume,
      commission,
      memberSplit,
      margin: m,
      marginRate: commission !== 0 ? m / commission : null,
      boost: boostForEntity(key),
    };
  });

  const ranked = rows
    .filter((r) => r.volume >= VOLUME_FLOOR_TXNS)
    // Default: margin rate ascending, so the thinnest retained cut sits on top.
    // Null rates (zero commission) sort last.
    .sort((a, b) => {
      if (a.marginRate == null) return 1;
      if (b.marginRate == null) return -1;
      return a.marginRate - b.marginRate;
    });
  const lowVolume = rows
    .filter((r) => r.volume < VOLUME_FLOOR_TXNS)
    .sort((a, b) => a.name.localeCompare(b.name));

  const medianMarginRate = median(
    ranked.map((r) => r.marginRate).filter((v): v is number => v != null),
  );

  return {
    market,
    currency,
    confirmedMargin,
    welcomeBonus,
    contribution,
    pendingMargin,
    volume: confirmed.length,
    medianMarginRate,
    ranked,
    lowVolume,
    trend: buildTrend(market, txns, input.welcomeBonusFundings, opts.now),
  };
}

/**
 * The two-line trend: confirmed margin and contribution over the last
 * TREND_LOOKBACK_MONTHS complete months plus the current month to date. Returns
 * null when fewer than TREND_MIN_PERIODS buckets have real confirmed volume.
 */
function buildTrend(
  market: Market,
  txns: Transaction[],
  welcome: WelcomeBonusFunding[],
  now: number,
): EarningsTrendPoint[] | null {
  const d = new Date(now);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const points: EarningsTrendPoint[] = [];

  for (let k = TREND_LOOKBACK_MONTHS; k >= 0; k--) {
    const start = Date.UTC(y, m - k, 1);
    // The current bucket (k === 0) runs only to `now`; complete months run to
    // the first instant of the following month.
    const end = k === 0 ? now : Date.UTC(y, m - k + 1, 1) - 1;
    const bd = new Date(start);
    const confirmedRows = txns.filter(
      (t) =>
        CONFIRMED_STATUSES.includes(t.status) &&
        inWindow(Date.parse(t.occurredAt), { start, end }),
    );
    const confirmedMargin = round2(
      confirmedRows.reduce(
        (acc, t) => acc + Number(t.commissionAmount) - Number(t.cashbackAmount),
        0,
      ),
    );
    const welcomeTotal = round2(
      welcome
        .filter(
          (f) =>
            f.market === market &&
            inWindow(Date.parse(f.fundedAt), { start, end }),
        )
        .reduce((acc, f) => acc + Number(f.amount), 0),
    );
    points.push({
      period: `${bd.getUTCFullYear()}-${String(bd.getUTCMonth() + 1).padStart(2, '0')}`,
      label: `${MONTHS[bd.getUTCMonth()]} ${String(bd.getUTCFullYear()).slice(2)}`,
      confirmedMargin,
      contribution: round2(confirmedMargin - welcomeTotal),
      hasVolume: confirmedRows.length > 0,
    });
  }

  const periodsWithVolume = points.filter((p) => p.hasVolume).length;
  return periodsWithVolume < TREND_MIN_PERIODS ? null : points;
}
